import RiAlertLine from '@ben/icons/alert-line';
import RiCloseLargeFill from '@ben/icons/close-large-fill';
import RiInformationLine from '@ben/icons/information-line';
import RiQuestionLine from '@ben/icons/question-line';
import type { ActionIconProps } from '@mantine/core';
import { ActionIcon, createPolymorphicComponent } from '@mantine/core';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { DefaultTheme, StyledComponent } from 'styled-components';
import styled, { css } from 'styled-components';
import { variant } from 'styled-system';
import { TypographyVariant } from '../../utils/theme';
import Button, { ButtonVariant } from '../deprecated-button/button';
import RIcon, { type RIconComponent } from '../remix-icon';
import Typography from '../typography/typography';

const VARIANT_ICON = {
  clarification: RiQuestionLine,
  information: RiInformationLine,
  critical: RiAlertLine,
  warning: RiAlertLine,
  amber: RiAlertLine,
  success: RiAlertLine,
};

export type BannerVariant = keyof typeof VARIANT_ICON;

export interface BannerProps {
  variant?: BannerVariant;
  icon?: RIconComponent;
  title?: string;
  message?: string;
  children?: ReactNode;
  button?: {
    label: string;
    onClick: () => void;
    variant?: ButtonVariant;
    width?: string;
    isLoading?: boolean;
    disabled?: boolean;
    'data-testid'?: string;
  };
  onClick?: () => void;
  onClickClose?: () => void;
  hideIcon?: boolean;
  className?: string;
  rightSection?: ReactNode;
}

export const Banner = ({
  variant = 'clarification',
  icon,
  title,
  message,
  children,
  button,
  onClick,
  onClickClose,
  hideIcon,
  className,
  rightSection,
}: BannerProps) => {
  const { t } = useTranslation();

  return (
    <Wrapper
      variant={variant}
      role={variant === 'critical' ? 'alert' : undefined}
      onClick={onClick}
      className={className}
    >
      {!hideIcon && (
        <RIcon
          icon={icon ?? VARIANT_ICON[variant]}
          size="md"
          aria-hidden="true"
          focusable="false"
        />
      )}
      <Content>
        {title && (
          <Typography variant={TypographyVariant.button} color="inherit">
            {title}
          </Typography>
        )}
        {message && (
          <Typography variant={TypographyVariant.bodyMd1} color="inherit">
            {message}
          </Typography>
        )}
        {children}
      </Content>
      {button && (
        <Button
          {...button}
          variant={button.variant ?? getButtonVariant(variant)}
          width={button.width ?? 'auto'}
        />
      )}
      {rightSection}
      {onClickClose && (
        <CloseButton
          variant="transparent"
          aria-label={t('words.close')}
          onClick={onClickClose}
          color="black"
        >
          <RIcon icon={RiCloseLargeFill} />
        </CloseButton>
      )}
    </Wrapper>
  );
};

export default Banner;

const getButtonVariant = (variant: BannerVariant) => {
  if (variant === 'critical') {
    return ButtonVariant.error;
  }
  return ButtonVariant.primary;
};

const baseStyle = ({ theme }: { theme: DefaultTheme }) => css`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing(2)};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing(2, 2.5)};

  > svg {
    flex-shrink: 0;
  }
`;

const variantStyle = ({ theme }: { theme: DefaultTheme }) =>
  variant({
    variants: {
      clarification: {
        border: `1px solid ${theme.palette.neutral[60]}`,
        backgroundColor: theme.palette.neutral[40],
        color: theme.palette.neutral[100],
      },
      information: {
        border: `1px solid ${theme.palette.primary.light}`,
        backgroundColor: theme.palette.neutral[10],
        color: theme.palette.neutral[100],
        '> svg': {
          color: theme.palette.primary.main,
        },
      },
      critical: {
        border: `1px solid ${theme.palette.error.light}`,
        backgroundColor: theme.palette.error.bg,
        color: theme.palette.error.main,
      },
      warning: {
        border: `1px solid ${theme.palette.warning.light}`,
        backgroundColor: theme.palette.warning.bg,
        color: theme.palette.neutral[100],
        '> svg': {
          color: theme.palette.warning.main,
        },
      },
      amber: {
        border: `1px solid ${theme.palette.amber.light}`,
        backgroundColor: theme.palette.amber.bg,
        color: theme.palette.neutral[100],
        '> svg': {
          color: theme.palette.amber.main,
        },
      },
      success: {
        border: `1px solid ${theme.palette.success.main}`,
        backgroundColor: theme.palette.success.bg,
        color: theme.palette.neutral[100],
        '> svg': {
          color: theme.palette.success.main,
        },
      },
    },
  });

const Wrapper: StyledComponent<
  'div',
  DefaultTheme,
  { variant: BannerVariant }
> = styled('div')(baseStyle, variantStyle);

const Content = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
    flex: 1;
    min-width: 0;
  `}
`;

const CloseButton = createPolymorphicComponent<'button', ActionIconProps>(
  styled(ActionIcon)`
    ${({ theme }) => css`
      align-self: flex-start;
      // Negative margins to tuck it in to the corner nicely
      margin-top: ${theme.spacing(-0.5)};
      margin-right: ${theme.spacing(-1)};
    `}
  `
);
